/**
 * Piano Lab Compare — Dynamic piano selector and comparison.
 * Custom element that handles search, product fetching, and dynamic table rendering.
 */

if (!customElements.get('piano-lab-compare')) {
  class PianoLabCompare extends HTMLElement {
    constructor() {
      super();
      this.products = [];
      this.collectionHandle = this.dataset.collection || 'digital-pianos';
    }

    connectedCallback() {
      this.selector = this.closest('.piano-lab')?.querySelector('[data-piano-lab-selector]');
      if (!this.selector) return;

      // Dynamically find all inputs/dropdowns (supports 2, 3, or 4 pianos)
      this.inputs = [];
      this.dropdowns = [];
      for (let i = 0; i < 4; i++) {
        const input = this.selector.querySelector(`[data-piano-lab-input="${i}"]`);
        const dropdown = this.selector.querySelector(`[data-piano-lab-dropdown="${i}"]`);
        if (input) this.inputs.push(input);
        if (dropdown) this.dropdowns.push(dropdown);
      }
      this.selected = new Array(this.inputs.length).fill(null);
      this.emptyState = this.querySelector('[data-piano-lab-empty]');
      this.dynamicTable = this.querySelector('[data-piano-lab-dynamic-table]');
      this.dynamicThead = this.querySelector('[data-piano-lab-thead]');
      this.dynamicTbody = this.querySelector('[data-piano-lab-tbody]');

      this.fetchProducts();
      this.bindEvents();
      this.loadFromUrl();
    }

    async fetchProducts() {
      try {
        const res = await fetch(`/collections/${this.collectionHandle}/products.json?limit=250`);
        if (!res.ok) return;
        const data = await res.json();
        this.products = data.products || [];
      } catch (e) {
        console.warn('Piano Lab: Could not fetch products', e);
      }
    }

    bindEvents() {
      this.inputs.forEach((input, idx) => {
        if (!input) return;

        input.addEventListener('focus', () => {
          this.showDropdown(idx, input.value);
        });

        input.addEventListener('input', () => {
          this.showDropdown(idx, input.value);
        });

        input.addEventListener('keydown', (e) => {
          if (e.key === 'Escape') {
            this.hideDropdown(idx);
            input.blur();
          }
        });
      });

      document.addEventListener('click', (e) => {
        this.dropdowns.forEach((dd, idx) => {
          if (dd && !dd.contains(e.target) && !this.inputs[idx]?.contains(e.target)) {
            this.hideDropdown(idx);
          }
        });
      });
    }

    showDropdown(idx, query) {
      const dd = this.dropdowns[idx];
      if (!dd) return;

      const otherHandle = this.selected[1 - idx]?.handle;
      const q = (query || '').toLowerCase().trim();

      const filtered = this.products.filter((p) => {
        if (p.handle === otherHandle) return false;
        if (!q) return true;
        return (
          p.title.toLowerCase().includes(q) ||
          p.vendor.toLowerCase().includes(q) ||
          p.handle.toLowerCase().includes(q)
        );
      });

      if (filtered.length === 0) {
        dd.innerHTML = '<div class="piano-lab__selector-no-results">No results</div>';
      } else {
        dd.innerHTML = filtered
          .slice(0, 20)
          .map((p) => {
            const img = p.images?.[0]?.src
              ? `<img src="${p.images[0].src}&width=80" class="piano-lab__selector-option-img" alt="" loading="lazy">`
              : '';
            return `<button class="piano-lab__selector-option" data-handle="${p.handle}" role="option">
              ${img}
              <span><span class="piano-lab__selector-option-brand">${this.escapeHtml(p.vendor)}</span> ${this.escapeHtml(p.title)}</span>
            </button>`;
          })
          .join('');
      }

      dd.classList.add('is-open');

      dd.querySelectorAll('.piano-lab__selector-option').forEach((btn) => {
        btn.addEventListener('click', () => {
          const handle = btn.dataset.handle;
          const product = this.products.find((p) => p.handle === handle);
          if (product) this.selectProduct(idx, product);
        });
      });
    }

    hideDropdown(idx) {
      this.dropdowns[idx]?.classList.remove('is-open');
    }

    selectProduct(idx, product) {
      this.selected[idx] = product;
      if (this.inputs[idx]) {
        this.inputs[idx].value = `${product.vendor} ${product.title}`;
      }
      this.hideDropdown(idx);
      this.updateUrl();

      if (this.selected[0] && this.selected[1]) {
        this.renderDynamicComparison();
      }
    }

    loadFromUrl() {
      const params = new URLSearchParams(window.location.search);
      const handles = [params.get('piano1'), params.get('piano2')];

      if (!handles[0] && !handles[1]) return;

      const waitForProducts = () => {
        if (this.products.length === 0) {
          setTimeout(waitForProducts, 200);
          return;
        }
        handles.forEach((handle, idx) => {
          if (!handle) return;
          const product = this.products.find((p) => p.handle === handle);
          if (product) this.selectProduct(idx, product);
        });
      };
      waitForProducts();
    }

    updateUrl() {
      const params = new URLSearchParams(window.location.search);
      if (this.selected[0]) params.set('piano1', this.selected[0].handle);
      if (this.selected[1]) params.set('piano2', this.selected[1].handle);
      const newUrl = `${window.location.pathname}?${params.toString()}`;
      window.history.replaceState({}, '', newUrl);
    }

    renderDynamicComparison() {
      if (!this.dynamicTable || !this.dynamicThead || !this.dynamicTbody) return;

      const [a, b] = this.selected;
      if (!a || !b) return;

      // Show table, hide empty state
      if (this.emptyState) this.emptyState.style.display = 'none';
      this.dynamicTable.style.display = '';

      // Render header
      this.dynamicThead.innerHTML = `<tr>
        <td class="piano-lab__spec-label-cell" style="border-bottom: 0.1rem solid rgba(var(--color-foreground), 0.08);"></td>
        ${[a, b].map((p) => this.renderProductHeader(p)).join('')}
      </tr>`;

      // We need metafields from the metaobject, which aren't in the products.json response.
      // Fetch each product's full page to get metafields via the section rendering approach.
      // For now, render with basic product info and note that metaobject specs require
      // the Liquid-rendered (pre-configured) mode or a Storefront API integration.
      this.dynamicTbody.innerHTML = `
        <tr>
          <td colspan="3" style="padding: 3rem; text-align: center; color: rgba(var(--color-foreground), 0.5); font-size: 1.4rem;">
            <p style="margin: 0 0 1rem;"><strong>${this.escapeHtml(a.vendor)} ${this.escapeHtml(a.title)}</strong> vs <strong>${this.escapeHtml(b.vendor)} ${this.escapeHtml(b.title)}</strong></p>
            <p style="margin: 0 0 2rem;">To see the full specifications comparison, ask your administrator to pre-configure this comparison in the theme editor.</p>
            <div style="display: flex; gap: 1rem; justify-content: center; flex-wrap: wrap;">
              <a href="${a.url || '/products/' + a.handle}" class="piano-lab__cta-btn">Voir ${this.escapeHtml(a.title)} →</a>
              <a href="${b.url || '/products/' + b.handle}" class="piano-lab__cta-btn">Voir ${this.escapeHtml(b.title)} →</a>
            </div>
          </td>
        </tr>
      `;
    }

    renderProductHeader(product) {
      const imgSrc = product.images?.[0]?.src
        ? `<img src="${product.images[0].src}&width=280" alt="${this.escapeHtml(product.title)}" class="piano-lab__product-img" loading="lazy" width="140" height="140">`
        : '<div class="piano-lab__product-placeholder">🎹</div>';

      const price = product.variants?.[0]?.price
        ? `<p class="piano-lab__product-price">$${(parseFloat(product.variants[0].price) / 100).toFixed(2)}</p>`
        : '';

      return `<td class="piano-lab__product-header">
        ${imgSrc}
        <p class="piano-lab__product-brand">${this.escapeHtml(product.vendor)}</p>
        <p class="piano-lab__product-model">${this.escapeHtml(product.title)}</p>
        ${price}
      </td>`;
    }

    escapeHtml(str) {
      const div = document.createElement('div');
      div.textContent = str || '';
      return div.innerHTML;
    }
  }

  customElements.define('piano-lab-compare', PianoLabCompare);
}

/* Category accordion toggle */
document.addEventListener('click', (e) => {
  const categoryRow = e.target.closest('.piano-lab__category-row');
  if (!categoryRow) return;

  categoryRow.classList.toggle('is-collapsed');

  // Find all following spec rows until the next category row
  let sibling = categoryRow.nextElementSibling;
  while (sibling && !sibling.classList.contains('piano-lab__category-row')) {
    if (sibling.classList.contains('piano-lab__spec-row')) {
      sibling.style.display = categoryRow.classList.contains('is-collapsed') ? 'none' : '';
    }
    sibling = sibling.nextElementSibling;
  }
});
